import { gql, MutationHookOptions, useMutation } from '@apollo/client';
import { portalPaths } from '@provision/portal-routes';
import { useCallback } from 'react';
import type { ContactSupportFormValues } from 'src/components/ContactSupportModal/contact-support-form.types';
import { Records } from 'src/graphql/types';
import { getPartnerFriendlyIdentifier } from 'src/routes/hooks/use-sync-partner-url-access';
import { useNotifications } from 'src/utils/hooks';
import { useFileUpload } from './files-upload';
import { usePartners } from './partner';

export const SUPPORT_PARTNER_ID = 2;

const CONTACT_SUPPORT_MUTATION = gql`
  mutation ContactSupport($params: ContactSupportInput!) {
    contactSupport(params: $params) {
      conversationId
      conversationMessageId
    }
  }
`;

export enum SupportIssueType {
  CONVERSATION_QA_REQUEST = 'Conversation QA Request',
  GENERAL_QUESTION = 'General Question',
  FEATURE_REQUEST = 'Feature Request',
  MARKETING_REQUEST = 'Marketing Request',
  TECHNICAL_SUPPORT = 'Technical Support',
}

export interface ContactSupportInput {
  issueType: SupportIssueType;
  conversationId: string;
  additionalContext: string;
  subject?: string;
  attachments: ContactSupportAttachment[];
}

interface ContactSupportAttachment {
  name: string;
  s3Url: string;
  size: number;
  type: string;
}

interface ContactSupportMutation {
  contactSupport: Records<{
    conversationId: string;
    conversationMessageId: string;
  }>;
}

export const useContactSupport = (options?: MutationHookOptions<ContactSupportMutation>) => {
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const { uploadFile } = useFileUpload();
  const { partners } = usePartners();

  const [contactSupportMutation] = useMutation<ContactSupportMutation>(
    CONTACT_SUPPORT_MUTATION,
    {
      ...options,
      onCompleted: () => {
        showSuccessAlert('Your message has been sent!');
      },
      onError: () => {
        showErrorAlert(
          'Something went wrong while trying to contact support. Please try again later.',
        );
      },
    },
  );

  const contactSupport = useCallback(
    async (input: ContactSupportFormValues) => {
      const linkedConversationPartner = input.linkedConversation
        ? partners?.[input.linkedConversation.partnerId]
        : null;

      const payload: ContactSupportInput = {
        conversationId: crypto.randomUUID(),
        issueType: input.issueType,
        subject: input.subject,
        additionalContext: formatAdditionalContext({
          ...input,
          partnerPrefix: linkedConversationPartner
            ? getPartnerFriendlyIdentifier(linkedConversationPartner)
            : null,
        }),
        attachments: [],
      };

      // Uploads must be performed one by one to avoid requests being combined
      for (const file of Array.from(input.attachments || [])) {
        try {
          const url = await uploadFile({
            file,
            fileKey: `${SUPPORT_PARTNER_ID}/${payload.conversationId}`,
            hasAttachments: true,
          });

          if (url) {
            payload.attachments.push({
              s3Url: url,
              name: file.name,
              size: file.size,
              type: file.type,
            });
          }
        } catch (error) {
          console.error(error);
        }
      }

      const { data } = await contactSupportMutation({
        variables: {
          params: payload,
        },
      });

      return data?.contactSupport;
    },
    [contactSupportMutation, partners, uploadFile],
  );

  return {
    contactSupport,
  };
};

// formatAdditionalContext function extends additional context with appended URL to linked conversation if such exists.
function formatAdditionalContext(
  input: ContactSupportFormValues & { partnerPrefix: string | null },
): string {
  let additionalContext = input.additionalContext;
  const partnerPrefix = input.partnerPrefix ? `/${input.partnerPrefix}` : '';
  if (input.linkedConversation) {
    additionalContext += `<br><br><span>Linked Conversation: <a href="${window.location.origin}${partnerPrefix}${portalPaths.conversation.full(input.linkedConversation.friendlyId)}" target="_blank">${input.linkedConversation.friendlyId}</a></span>`;
  }
  return additionalContext;
}
